/**
 * Web Vitals Callback Function
 *
 * This function receives performance metrics from the web-vitals library
 * and sends them to Amplitude for monitoring and analysis.
 *
 * Metrics tracked:
 * - CLS (Cumulative Layout Shift): Visual stability
 * - INP (Interaction to Next Paint): Responsiveness
 * - FCP (First Contentful Paint): Initial paint time
 * - LCP (Largest Contentful Paint): Main content load time
 * - TTFB (Time to First Byte): Server response time
 */

import amplitude from 'amplitude-js';

// Import the Metric type from web-vitals
type Metric = {
    name: 'CLS' | 'FCP' | 'INP' | 'LCP' | 'TTFB';
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    delta: number;
    id: string;
    navigationType: 'navigate' | 'reload' | 'back-forward' | 'back-forward-cache' | 'prerender' | 'restore';
    entries: PerformanceEntry[];
};

export const sendToAmplitude = (metric: Metric) => {
    // Only track in production to avoid polluting analytics with dev data
    if (process.env.NODE_ENV !== 'production') {
        console.log('Web Vital:', metric.name, metric.value, metric.rating);
        return;
    }

    // Send to Amplitude
    try {
        const amplitudeClient = amplitude.getInstance();

        amplitudeClient.logEvent('web_vitals', {
            // Core metric data
            metric_name: metric.name, // 'CLS', 'INP', 'FCP', 'LCP', 'TTFB'
            metric_value: Math.round(metric.value), // Round to avoid decimals
            metric_rating: metric.rating, // 'good', 'needs-improvement', 'poor'
            metric_id: metric.id, // Unique metric instance ID
            metric_delta: Math.round(metric.delta), // Change from last report

            // Context data
            page_path: window.location.pathname, // Which page
            page_url: window.location.href, // Full URL
            navigation_type: metric.navigationType, // 'navigate', 'reload', 'back-forward', etc.

            // Your app-specific context (optional but valuable)
            country: localStorage.getItem('country') || 'unknown',
            user_role: localStorage.getItem('userRole') || 'unknown',
        });
    } catch (error) {
        console.error('Failed to send web vitals to Amplitude:', error);
    }
};

/**
 * Alternative: Send to Console (Development/Debugging)
 * Use this during development to see metrics in real-time
 */
export const logToConsole = (metric: Metric) => {
    console.group(`📊 Web Vital: ${metric.name}`);
    console.log('Value:', metric.value);
    console.log('Rating:', metric.rating);
    console.log('Delta:', metric.delta);
    console.log('ID:', metric.id);
    console.log('Navigation Type:', metric.navigationType);
    console.log('Entries:', metric.entries);
    console.groupEnd();
};

/**
 * Advanced: Send to Multiple Destinations
 * Use this if you want both Amplitude and Google Analytics
 */
export const sendToMultipleDestinations = (metric: Metric) => {
    // Send to Amplitude
    sendToAmplitude(metric);

    // Send to Google Analytics (if you have it)
    if (typeof (window as any).gtag !== 'undefined') {
        (window as any).gtag('event', metric.name, {
            value: Math.round(metric.delta),
            metric_id: metric.id,
            metric_value: Math.round(metric.value),
            metric_rating: metric.rating,
        });
    }
};

/**
 * Recommended: Conditional Callback
 * Logs to console in dev, sends to Amplitude in production
 */
export const reportWebVitalsCallback = (metric: Metric) => {
    if (process.env.NODE_ENV === 'production') {
        sendToAmplitude(metric);
    } else {
        logToConsole(metric);
    }
};
